/**
 * useMenuLink Hooks
 * Shared navigation for main-menu links (header nav and homepage "Find more" buttons)
 */
import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMainMenu } from '@hooks/useMenu';
import { ROUTES } from '@core/constants/routes.constants';
import { isHomeSectionHash, scrollToHomeSectionWithRetry } from '@utils/scroll.utils';

/**
 * Returns a function that opens a menu link the same way the header navigation does:
 * external → new tab, "#section" → scroll on home page, otherwise → router navigation
 */
export const useOpenMenuLink = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (href: string, isExternal: boolean) => {
      if (isExternal) {
        // Open external links in new tab
        window.open(href, '_blank', 'noopener,noreferrer');
      } else if (href.startsWith('#')) {
        const elementId = href.substring(1);

        if (location.pathname !== ROUTES.HOME && location.pathname !== '/') {
          navigate({ pathname: ROUTES.HOME, hash: elementId });
          return;
        }

        if (isHomeSectionHash(elementId)) {
          scrollToHomeSectionWithRetry(elementId);
          return;
        }

        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else if (href === '/' || href === ROUTES.HOME) {
        navigate(ROUTES.HOME);
      } else {
        navigate(href);
      }
    },
    [navigate, location.pathname]
  );
};

/**
 * Finds a main-menu link by its title (case-insensitive) and returns a click handler
 * that behaves like clicking that menu item. Falls back to `fallbackHref` while the
 * menu is loading or if no matching item exists.
 */
export const useMainMenuLink = (title: string, fallbackHref: string) => {
  const { data: menuItems = [] } = useMainMenu();
  const openMenuLink = useOpenMenuLink();

  const menuItem = menuItems.find(
    (item) => item.title.trim().toLowerCase() === title.trim().toLowerCase()
  );
  const href = menuItem?.href ?? fallbackHref;
  const isExternal = menuItem?.isExternal ?? false;

  const open = useCallback(() => openMenuLink(href, isExternal), [openMenuLink, href, isExternal]);

  return { href, isExternal, open };
};
